import {
  areaGate,
  documentsOf,
  visibleReports,
  type EventFeatureKey,
  type EventStatus,
  type Permission,
} from '@barso/shared';
import type { IconName } from './icons';

/**
 * Gruppen des Basar-Menüs (Reihenfolge = Reihenfolge im Menü). Die Übersicht steht allein vorn, „Mehr“ sammelt, was
 * selten gebraucht wird (Einrichtung, Einstellungen). Fünf Hauptpunkte plus „Mehr“.
 */
export type AreaGroup =
  'start' | 'vorbereitung' | 'basartag' | 'kommunikation' | 'abschluss' | 'mehr';

export const AREA_GROUPS: { key: Exclude<AreaGroup, 'start'>; title: string }[] = [
  { key: 'vorbereitung', title: 'Vorbereitung' },
  { key: 'basartag', title: 'Basartag' },
  { key: 'kommunikation', title: 'Kommunikation' },
  { key: 'abschluss', title: 'Abschluss' },
  { key: 'mehr', title: 'Mehr' },
];

export interface Area {
  key: string;
  /** Pfad relativ zu `/basare/:eventId` (leer = Übersicht). */
  path: string;
  /** Name in Menü, Kachel und Seitentitel (eine Quelle, Glossar `docs/15-GLOSSAR.md`). */
  title: string;
  /** Kurzname für enge Stellen (Anzeige der aktiven Seite in der Menüleiste). */
  short: string;
  icon: IconName;
  /** Wozu der Bereich da ist (fester Zweck, Satzfragment ohne Punkt). */
  description: string;
  group: AreaGroup;
  /** Ohne Angabe: für alle mit Zugriff auf den Basar. */
  permission?: (permissions: Permission[]) => boolean;
  /**
   * Zusatzfunktion des Basars (Plan Welle 3, F0): Der Bereich erscheint nur bei eingeschalteter Funktion, ohne Angabe
   * immer. Das Recht gilt zusätzlich.
   */
  feature?: EventFeatureKey;
  /**
   * Nur in diesen Basar-Status anzeigen (nur Anzeige, der Server prüft selbst). Ohne Angabe in jedem Status. Ohne
   * bekannten Status (Basar lädt noch) bleibt der Bereich ausgeblendet.
   */
  openIn?: (status: EventStatus) => boolean;
}

/** Schalterstände der Zusatzfunktionen eines Basars (aus der Basar-Antwort), fehlende gelten als aus. */
export type AreaFeatures = Partial<Record<EventFeatureKey, boolean>>;

/** Ist die Zusatzfunktion des Bereichs eingeschaltet? Bereiche ohne Zusatzfunktion: immer. */
export function areaFeatureOn(area: Area, features: AreaFeatures | undefined): boolean {
  return area.feature === undefined || features?.[area.feature] === true;
}

/** Die Schalter aus der Basar-Antwort (fehlende Felder älterer Antworten gelten als aus). */
export function featuresOfEvent(event: {
  runSheetEnabled?: boolean;
  pushEnabled?: boolean;
  payoutTransferEnabled?: boolean;
  helperBonusEnabled?: boolean;
}): AreaFeatures {
  return {
    runSheet: event.runSheetEnabled === true,
    push: event.pushEnabled === true,
    payoutTransfer: event.payoutTransferEnabled === true,
    helperBonus: event.helperBonusEnabled === true,
  };
}

const has = (permission: Permission) => (permissions: Permission[]) =>
  permissions.includes(permission);

/** Alle Bereiche eines Basars. Reihenfolge innerhalb einer Gruppe = Reihenfolge im Menü. */
export const AREAS = {
  start: {
    key: 'start',
    path: '',
    title: 'Übersicht',
    short: 'Übersicht',
    icon: 'home',
    description: 'Stand des Basars und alle Bereiche auf einen Blick',
    group: 'start',
  },
  zeitfenster: {
    key: 'zeitfenster',
    path: '/zeitfenster',
    title: 'Zeitfenster',
    short: 'Zeitfenster',
    icon: 'clock',
    description: 'Abgabe- und Abholzeiten für Verkäufer',
    group: 'mehr',
    permission: has('event.manage'),
  },
  verkaeufer: {
    key: 'verkaeufer',
    path: '/verkaeufer',
    title: 'Verkäufer',
    short: 'Verkäufer',
    icon: 'register',
    description: 'Anmeldungen freigeben, Nummern vergeben',
    group: 'vorbereitung',
    permission: has('event.sellers.approve'),
  },
  team: {
    key: 'team',
    path: '/team',
    title: 'Team',
    short: 'Team',
    icon: 'users',
    description: 'Rollen vergeben: Verwaltung, Annahme, Kasse, Helfer',
    group: 'vorbereitung',
    permission: has('event.team.manage'),
  },
  helfer: {
    key: 'helfer',
    path: '/helfer',
    title: 'Helfer',
    short: 'Helfer',
    icon: 'qr',
    description: 'Einladungslink mit Rolle, Freigaben und Aushänge',
    group: 'vorbereitung',
    permission: has('event.team.manage'),
  },
  teilen: {
    key: 'teilen',
    path: '/teilen',
    title: 'Anmeldung teilen',
    short: 'Teilen',
    icon: 'share',
    description: 'Link und QR-Code für die Anmeldung',
    group: 'vorbereitung',
    permission: has('event.manage'),
    openIn: (status) => areaGate('teilen', status, undefined, []).enabled,
  },
  helferschichten: {
    key: 'helferschichten',
    path: '/helferschichten',
    title: 'Helferschichten',
    short: 'Schichten',
    icon: 'calendar',
    description: 'Schichten planen, Eintragung öffnen, Anfragen beantworten',
    group: 'vorbereitung',
    permission: has('event.team.manage'),
  },
  kassenplaetze: {
    key: 'kassenplaetze',
    path: '/kassenplaetze',
    title: 'Kassen einrichten',
    short: 'Kassen einrichten',
    icon: 'monitor',
    description: 'Kassen anlegen und Geräte mit ihnen verbinden',
    group: 'mehr',
    permission: has('event.manage'),
  },
  plakat: {
    key: 'plakat',
    path: '/plakat',
    title: 'Plakat',
    short: 'Plakat',
    icon: 'printer',
    description: 'Aushang mit QR-Code erstellen oder eigenes hochladen',
    group: 'mehr',
    permission: has('event.manage'),
  },
  einstellungen: {
    key: 'einstellungen',
    path: '/einstellungen',
    title: 'Einstellungen',
    short: 'Einstellungen',
    icon: 'settings',
    description: 'Stammdaten, Anmeldung, Artikel, Kasse, Helfer und Abrechnung',
    group: 'mehr',
    permission: has('event.manage'),
  },
  annahme: {
    key: 'annahme',
    path: '/annahme',
    title: 'Warenannahme',
    short: 'Annahme',
    icon: 'tag',
    description: 'Etiketten scannen, Artikel als abgegeben erfassen',
    group: 'basartag',
    permission: has('event.items.receive'),
  },
  artikel: {
    key: 'artikel',
    path: '/artikel',
    title: 'Artikel suchen',
    short: 'Artikel',
    icon: 'search',
    description: 'Wem gehört dieser Artikel? Nach Titel, Code oder Nummer suchen',
    group: 'basartag',
    permission: has('event.items.receive'),
  },
  kasse: {
    key: 'kasse',
    path: '/kasse',
    title: 'Kasse',
    short: 'Kasse',
    icon: 'euro',
    description: 'Artikel scannen und bar kassieren, auch ohne Netz',
    group: 'basartag',
    permission: has('event.sales.create'),
  },
  rueckgabe: {
    key: 'rueckgabe',
    path: '/rueckgabe',
    title: 'Rückgabe',
    short: 'Rückgabe',
    icon: 'blocks',
    description: 'Nicht verkaufte Artikel an die Verkäufer zurückgeben',
    group: 'basartag',
    permission: has('event.returns.sort'),
  },
  pruefen: {
    key: 'pruefen',
    path: '/pruefen',
    title: 'Artikel prüfen',
    short: 'Prüfen',
    icon: 'check',
    description: 'Code scannen und Artikel ansehen, ohne Preise',
    group: 'basartag',
    permission: has('event.items.inspect'),
  },
  laufzettel: {
    key: 'laufzettel',
    path: '/laufzettel',
    title: 'Vorab scannen',
    short: 'Vorab',
    icon: 'scan',
    description: 'Etiketten schon in der Schlange scannen, die Kasse übernimmt den Korb',
    group: 'basartag',
    permission: has('event.items.inspect'),
    feature: 'runSheet',
  },
  einlass: {
    key: 'einlass',
    path: '/einlass',
    title: 'Einlass',
    short: 'Einlass',
    icon: 'shield',
    description: 'Einlass-Pass scannen: gilt ab einer Uhrzeit, ohne Namen',
    group: 'basartag',
    permission: has('event.entry.check'),
    feature: 'helperBonus',
  },
  bargeld: {
    key: 'bargeld',
    path: '/bargeld',
    title: 'Bargeld',
    short: 'Bargeld',
    icon: 'coins',
    description: 'Wechselgeld, Entnahmen und Zählen der eigenen Kasse',
    group: 'basartag',
    permission: has('event.cash.count.own'),
  },
  uebersicht: {
    key: 'uebersicht',
    path: '/uebersicht',
    title: 'Kassenstand',
    short: 'Kassenstand',
    icon: 'sync',
    description: 'Stand jeder Kasse, Abgleich, Preisabweichungen und Rabattaktion',
    group: 'basartag',
    permission: has('event.reports.view'),
  },
  nachrichten: {
    key: 'nachrichten',
    path: '/nachrichten',
    title: 'Nachrichten',
    short: 'Nachrichten',
    icon: 'mail',
    description: 'Fragen von Verkäufern und Helfern beantworten',
    group: 'kommunikation',
    permission: has('event.messages.manage'),
  },
  neuigkeiten: {
    key: 'neuigkeiten',
    path: '/neuigkeiten',
    title: 'Neuigkeiten',
    short: 'Neuigkeiten',
    icon: 'sparkle',
    description: 'Beiträge für Verkäufer, Helfer und Team',
    group: 'kommunikation',
    permission: has('event.news.publish'),
  },
  live: {
    key: 'live',
    path: '/live',
    title: 'Live-Stand',
    short: 'Live',
    icon: 'eye',
    description: 'Umsatz und Kassen im Überblick',
    group: 'abschluss',
    permission: has('event.reports.view'),
  },
  berichte: {
    key: 'berichte',
    path: '/berichte',
    title: 'Berichte',
    short: 'Berichte',
    icon: 'chart',
    description: 'Auswertungen mit Diagrammen und Excel-Download',
    group: 'abschluss',
    permission: (permissions) => visibleReports('event', { permissions }).length > 0,
  },
  dokumente: {
    key: 'dokumente',
    path: '/dokumente',
    title: 'Dokumente',
    short: 'Dokumente',
    icon: 'file',
    description: 'Alle Druckblätter und Listen an einem Ort',
    group: 'abschluss',
    permission: (permissions) => documentsOf('event', permissions).length > 0,
  },
  abrechnung: {
    key: 'abrechnung',
    path: '/abrechnung',
    title: 'Abrechnung',
    short: 'Abrechnung',
    icon: 'receipt',
    description: 'Verkaufsübersicht und Auszahlungen an Verkäufer',
    group: 'abschluss',
    permission: has('event.settlement.run'),
  },
} as const satisfies Record<string, Area>;

export type AreaKey = keyof typeof AREAS;

/** Alle Bereiche in Menüreihenfolge (Übersicht, Gruppen, Einstellungen). */
export const AREA_LIST: Area[] = Object.values(AREAS);

/** Bereiche, die die Person sehen darf (Recht, Zusatzfunktion und, wenn angegeben, Basar-Status). */
export function visibleAreas(
  permissions: Permission[],
  features?: AreaFeatures,
  status?: EventStatus,
): Area[] {
  return AREA_LIST.filter(
    (area) =>
      (area.permission === undefined || area.permission(permissions)) &&
      areaFeatureOn(area, features) &&
      (area.openIn === undefined || (status !== undefined && area.openIn(status))),
  );
}
